import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';

export default function VendorCalendar({ user, db, bookings, listings = [], navigateTo, onPlanRouteForDay }) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedItem, setSelectedItem] = useState('all');
    const [hoveredDay, setHoveredDay] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [blockedDates, setBlockedDates] = useState(user?.blockedDates || []);

    useEffect(() => {
        if (user?.blockedDates) {
            setBlockedDates(user.blockedDates);
        }
    }, [user]);

    const bookingsByDate = useMemo(() => {
        const dateMap = {};
        const filteredBookings = selectedItem === 'all' ? bookings : bookings.filter(b => b.listingId === selectedItem);
        filteredBookings.forEach(booking => {
            let currentDate = new Date(booking.date);
            currentDate = new Date(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate());
            let stopDate = new Date(booking.returnDate);
            stopDate = new Date(stopDate.getUTCFullYear(), stopDate.getUTCMonth(), stopDate.getUTCDate());
            while (currentDate <= stopDate) {
                const dateString = currentDate.toISOString().split('T')[0];
                if (!dateMap[dateString]) dateMap[dateString] = [];
                dateMap[dateString].push(booking);
                currentDate.setDate(currentDate.getDate() + 1);
            }
        });
        return dateMap;
    }, [bookings, selectedItem]);

    const handleBlockDate = async (dateString) => {
        if (!user || !user.uid) { console.error("User data not available to block date."); return; }
        const userRef = doc(db, "users", user.uid);
        if (blockedDates.includes(dateString)) {
            await updateDoc(userRef, { blockedDates: arrayRemove(dateString) });
        } else {
            await updateDoc(userRef, { blockedDates: arrayUnion(dateString) });
        }
    };

    const dailySummary = useMemo(() => {
        if (!hoveredDay || !bookingsByDate[hoveredDay]) return null;
        const summary = {};
        bookingsByDate[hoveredDay].forEach(booking => {
            summary[booking.listingTitle] = (summary[booking.listingTitle] || 0) + booking.quantity;
        });
        return summary;
    }, [hoveredDay, bookingsByDate]);

    const handleMonthChange = (offset) => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + offset);
            return newDate;
        });
    };
    
    const getCityFromAddress = (address) => {
        if (!address) return 'Pickup';
        const parts = address.split(',').map(part => part.trim());
        if (parts.length >= 3) return parts[1]; 
        if (parts.length === 2) return parts[0];
        return address;
    };
    
    const renderCalendar = (monthDate) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        let days = [];
        
        for (let i = 0; i < firstDay; i++) { 
            days.push(<div key={`empty-${i}`} className="w-full h-24"></div>); 
        }
        
        for (let day = 1; day <= daysInMonth; day++) {
            const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayBookings = bookingsByDate[dateString] || [];
            const isBlocked = blockedDates.includes(dateString);
            
            const activeBookings = dayBookings.filter(b => !['cancelled', 'declined'].includes(b.status));
            const hasCancelled = dayBookings.some(b => ['cancelled', 'declined'].includes(b.status));

            let baseClasses = "relative flex items-center justify-center h-24 text-sm cursor-pointer rounded-lg transition-colors";
            let stateClasses = 'bg-white hover:bg-gray-100';
            
            if (isBlocked) stateClasses = 'bg-gray-200 text-gray-400 line-through';
            else if (activeBookings.some(b => b.status === 'pending')) stateClasses = 'bg-yellow-100 hover:bg-yellow-200';
            else if (activeBookings.length > 0) stateClasses = 'bg-blue-100 hover:bg-blue-200';
            else if (hasCancelled) stateClasses = 'bg-gray-50 text-gray-400 line-through decoration-gray-400';

            if (selectedDate === dateString) stateClasses += ' ring-2 ring-pink-500';
            
            days.push(
                <div 
                    key={day} 
                    className={`${baseClasses} ${stateClasses}`} 
                    onMouseEnter={() => setHoveredDay(dateString)} 
                    onMouseLeave={() => setHoveredDay(null)} 
                    onClick={() => setSelectedDate(dateString)}
                >
                    <span className="font-semibold">{day}</span>
                    {hoveredDay === dateString && dailySummary && (
                        <div className="absolute z-10 bottom-full mb-2 w-48 bg-white rounded-lg shadow-xl animate-fade-in border border-gray-200 overflow-hidden">
                            <h4 className="font-bold p-2 bg-pink-50 text-pink-800 text-sm">Daily Summary</h4>
                            <div className="p-2 text-sm">
                                {Object.entries(dailySummary).map(([title, qty]) => (
                                    <p key={title} className="truncate">{qty} x {title}</p>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        return days;
    };
    
    return (
        <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-4 gap-2">
                <div className="flex justify-between items-center w-full sm:w-auto">
                    <button onClick={() => handleMonthChange(-1)} className="p-2 rounded-full hover:bg-gray-100">&lt;</button>
                    <h3 className="text-xl font-bold mx-4 whitespace-nowrap">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                    <button onClick={() => handleMonthChange(1)} className="p-2 rounded-full hover:bg-gray-100">&gt;</button>
                </div>
                <select value={selectedItem} onChange={(e) => setSelectedItem(e.target.value)} className="p-2 border rounded-md w-full sm:w-auto sm:max-w-xs">
                    <option value="all">All Items</option>
                    {listings.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
            </div>
            <div className="grid grid-cols-7 gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="font-bold text-center py-2 text-gray-500">{d}</div>)}
                {renderCalendar(currentMonth)}
            </div>
            {selectedDate && (
                <div className="mt-4 pt-4 border-t">
                    <h3 className="font-bold">Bookings for {selectedDate}</h3>
                    {(bookingsByDate[selectedDate] || []).length > 0 ? (
                        <>
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                {(bookingsByDate[selectedDate] || []).map(booking => {
                                    const isCancelled = ['cancelled', 'declined'].includes(booking.status);
                                    return (
                                        <li 
                                            key={booking.id} 
                                            onClick={() => navigateTo('bookingDetail', { booking })} 
                                            className={`cursor-pointer hover:text-pink-500 ${isCancelled ? 'line-through text-gray-400' : ''}`}
                                        >
                                            {booking.quantity} x {booking.listingTitle} for {booking.renterName} in <span className="font-semibold">{getCityFromAddress(booking.deliveryAddress)}</span>
                                            {booking.status === 'pending' && <span className="ml-2 text-xs font-bold text-yellow-600 bg-yellow-200 px-2 py-1 rounded-full no-underline inline-block">Needs Attention</span>}
                                            {isCancelled && <span className="ml-2 text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded-full no-underline inline-block">Cancelled</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="text-center mt-4">
                                <button onClick={() => onPlanRouteForDay(selectedDate)} className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition font-semibold">
                                    Plan Deliveries for this Day
                                </button>
                            </div>
                        </>
                    ) : <p className="text-gray-500 mt-2">No bookings for this day.</p>}
                </div>
            )}
        </div>
    );
}